import {
  AfterContentInit,
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Carousel } from './models/Carousel.interface';
import { IconOverrides } from './models/IconOverrides.interface';
import { ThemePalette } from '@angular/material/core';
import { CarouselSlideComponent } from './carousel-slide/carousel-slide.component';
import { ListKeyManager } from '@angular/cdk/a11y';
import { BehaviorSubject, Observable, Subject, interval } from 'rxjs';
import { animate, AnimationBuilder, style } from '@angular/animations';
import { filter, takeUntil } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { ArrayUtils } from '../../utils';
import { CAROUSEL_ORIENTATION } from './models/CarouselOrientation.enum';

enum Direction {
  Previous,
  Next,
  Index,
}

@Component({
  selector: 'lib-carousel',
  templateUrl: './carousel.component.html',
  styleUrl: './carousel.component.scss',
})
export class CarouselComponent
  implements AfterContentInit, AfterViewInit, Carousel, OnDestroy
{
  // Animations
  @Input() public timings = '250ms ease-in';

  @Input()
  public set autoplay(value: boolean) {
    this.autoplay$.next(value);
    this._autoplay = value;
  }

  @Input()
  public set interval(value: number) {
    this.interval$.next(value);
  }

  // Navigation
  public get loop(): boolean {
    return this._loop;
  }
  @Input()
  public set loop(value: boolean) {
    this.loop$.next(value);
    this._loop = value;
  }

  @Input() public hideArrows = true;
  @Input() public hideIndicators = true;
  @Input() public pauseOnHover = true;

  // Appearance
  @Input() public slidesPerView = 1;
  @Input() public gap = '10px';
  @Input() public color: ThemePalette = 'primary';
  @Input() public iconOverrides: IconOverrides;
  @Input() public maintainAspectRatio = true;
  @Input() public slideMinHeight = 'unset';

  // Accessibility
  @Input() public useKeyboard = false;
  @Input() public useMouseWheel = false;

  public get orientation(): CAROUSEL_ORIENTATION {
    return this._orientation;
  }
  @Input()
  public set orientation(value: CAROUSEL_ORIENTATION) {
    this.orientation$.next(value);
    this._orientation = value;
  }

  @Output()
  public animationStart: EventEmitter<number> = new EventEmitter<number>();

  public get currentIndex(): number {
    if (this.listKeyManager) {
      return this.listKeyManager.activeItemIndex;
    }

    return 0;
  }

  @ContentChildren(CarouselSlideComponent)
  public slidesList: QueryList<CarouselSlideComponent>;
  @ViewChild('carouselContainer')
  private carouselContainer: ElementRef<HTMLDivElement>;
  @ViewChild('carouselList') private carouselList: ElementRef<HTMLElement>;
  public listKeyManager: ListKeyManager<CarouselSlideComponent>;

  private _autoplay = true;
  private autoplay$ = new Subject<boolean>();

  private interval$ = new BehaviorSubject<number>(5000);

  private _loop = true;
  private loop$ = new Subject<boolean>();

  private _orientation: CAROUSEL_ORIENTATION = 'LEFT_TO_RIGHT';
  private orientation$ = new Subject<CAROUSEL_ORIENTATION>();

  private timer$: Observable<number>;
  private timerStop$ = new Subject<void>();

  private destroy$ = new Subject<void>();
  private playing = false;

  constructor(
    private animationBuilder: AnimationBuilder,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  public ngAfterContentInit(): void {
    this.listKeyManager = new ListKeyManager(this.slidesList)
      .withVerticalOrientation(this.isVerticalDirection(this.orientation))
      .withHorizontalOrientation(
        this.getKeyManagerHorizontalDirection(this.orientation)
      )
      .withWrap(this._loop);

    this.listKeyManager.updateActiveItem(0);
    this.listKeyManager.change
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.playAnimation());
  }

  public ngAfterViewInit(): void {
    this.autoplay$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.stopTimer();
      this.startTimer(value);
    });

    this.interval$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.stopTimer();
      this.resetTimer(value);
      this.startTimer(this._autoplay);
    });

    this.loop$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.listKeyManager.withWrap(value));

    this.orientation$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) =>
        this.listKeyManager
          .withVerticalOrientation(this.isVerticalDirection(value))
          .withHorizontalOrientation(
            this.getKeyManagerHorizontalDirection(value)
          )
      );
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get flexDirection(): string {
    if (this.orientation === 'RIGHT_TO_LEFT') {
      return 'row-reverse';
    }

    if (this.orientation === 'LEFT_TO_RIGHT') {
      return 'row';
    }

    if (this.orientation === 'BOTTOM_TO_TOP') {
      return 'column';
    }

    return 'column-reverse';
  }

  public isVerticalDirection(orientation: CAROUSEL_ORIENTATION): boolean {
    return orientation === 'TOP_TO_BOTTOM' || orientation === 'BOTTOM_TO_TOP';
  }

  public generateListOfIndicatorButton(): number[] {
    return ArrayUtils.getDummyArray(
      Math.ceil(this.slidesList.length / this.slidesPerView)
    );
  }

  public calculateHeightOfSlide(): string {
    if (!this.isVerticalDirection(this.orientation)) {
      return 'auto';
    }
    return `calc(100% / ${this.slidesPerView} - ${this.gap})`;
  }

  public calculateWidthOfSlide(): string {
    if (this.isVerticalDirection(this.orientation)) {
      return '100%';
    }
    return `calc(100% / ${this.slidesPerView} - ${this.gap})`;
  }

  public calculatePaddingOfSlide(): string {
    const isVerticalDirection = this.isVerticalDirection(this.orientation);

    const leftRightPadding = isVerticalDirection
      ? '0'
      : `calc(${this.gap} / 2)`;
    const topBottomPadding = isVerticalDirection
      ? `calc(${this.gap} / 2)`
      : '0';

    return `${topBottomPadding} ${leftRightPadding}`;
  }

  public next(): void {
    this.goto(Direction.Next);
  }

  public previous(): void {
    this.goto(Direction.Previous);
  }

  public slideTo(index: number): void {
    this.goto(Direction.Index, index);
  }

  @HostListener('keyup', ['$event'])
  public onKeyUp(event: KeyboardEvent): void {
    if (this.useKeyboard && !this.playing) {
      this.listKeyManager.onKeydown(event);
    }
  }

  @HostListener('mouseenter')
  public onMouseEnter(): void {
    if (this.pauseOnHover) {
      this.stopTimer();
    }
  }

  @HostListener('mouseleave')
  public onMouseLeave(): void {
    if (this.pauseOnHover) {
      this.startTimer(this._autoplay);
    }
  }

  @HostListener('mousewheel', ['$event'])
  public onMouseWheel(event: WheelEvent): void {
    if (this.useMouseWheel) {
      event.preventDefault(); // prevent window to scroll
      const delta = Math.sign(event.deltaY);

      if (delta > 0) {
        this.next();
      } else if (delta < 0) {
        this.previous();
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    // Reset carousel when window is resized
    // in order to avoid major glitches.
    this.slideTo(0);
  }

  public onPan(event: any, slideElem: HTMLElement): void {
    this.stopTimer();

    let delta;

    if (this.isVerticalDirection(this.orientation)) {
      if (Math.abs(event.velocityX) > Math.abs(event.velocityY)) {
        return;
      }

      delta = event.deltaY;
    } else {
      // https://github.com/angular/angular/issues/10541#issuecomment-346539242
      // if y velocity is greater, it's a panup/pandown, so ignore.
      if (Math.abs(event.velocityY) > Math.abs(event.velocityX)) {
        return;
      }

      delta = event.deltaX;
    }

    if (this.isOutOfBounds()) {
      delta *= 0.2; // decelerate movement;
    }

    this.renderer.setStyle(slideElem, 'cursor', 'grabbing');
    this.renderer.setStyle(
      this.carouselList.nativeElement,
      'transform',
      this.getTranslation(this.getOffset() + delta)
    );
  }

  public onPanEnd(event: any, slideElem: HTMLElement): void {
    // swift at least 25% size of container to trigger slide change
    const thresholdMovementPercentage = 0.25;

    this.startTimer(this._autoplay);

    this.renderer.removeStyle(slideElem, 'cursor');

    const isVerticalDirection = this.isVerticalDirection(this.orientation);

    const delta = isVerticalDirection ? event.deltaY : event.deltaX;
    const containerSize = isVerticalDirection
      ? this.getHeight()
      : this.getWidth();

    const slideSize = containerSize / this.slidesPerView;
    const numberOfFullySlideToSwift = Math.floor(Math.abs(delta) / slideSize);
    const isRestDeltaExceedThresholdMovement =
      delta - numberOfFullySlideToSwift * slideSize >
      containerSize * thresholdMovementPercentage;
    const totalNumberOfSlideToSwift =
      numberOfFullySlideToSwift + (isRestDeltaExceedThresholdMovement ? 1 : 0);

    const currentActiveIndex = this.currentIndex;
    // delta <= 0: go to next slide, otherwise go to previous slide
    const sign = delta <= 0 ? 1 : -1;
    const newActiveIndex = Math.min(
      currentActiveIndex + sign * totalNumberOfSlideToSwift,
      this.slidesList.length
    );

    if (this.isOutOfBounds() || currentActiveIndex === newActiveIndex) {
      this.playAnimation(); // slide back, don't change current index
      return;
    }

    this.slideTo(newActiveIndex);
  }

  private isOutOfBounds(): boolean {
    const sign = this.isReversedDirection(this.orientation) ? -1 : 1;
    const lastIndex = this.slidesList.length - 1;

    if (this.isVerticalDirection(this.orientation)) {
      const top =
        sign *
        (this.carouselList.nativeElement.getBoundingClientRect().top -
          this.carouselList.nativeElement.offsetParent.getBoundingClientRect()
            .top);
      const height = -this.getHeight() * lastIndex;

      return (
        (this.currentIndex === 0 && top >= 0) ||
        (this.currentIndex === lastIndex && top <= height)
      );
    } else {
      const left =
        sign *
        (this.carouselList.nativeElement.getBoundingClientRect().left -
          this.carouselList.nativeElement.offsetParent.getBoundingClientRect()
            .left);
      const width = -this.getWidth() * lastIndex;

      return (
        (this.currentIndex === 0 && left >= 0) ||
        (this.currentIndex === lastIndex && left <= width)
      );
    }
  }

  private isVisible(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const elem = this.carouselContainer.nativeElement;
    const docViewTop = window.scrollY;
    const docViewBottom = docViewTop + window.innerHeight;
    const elemOffset = elem.getBoundingClientRect();
    const elemTop = docViewTop + elemOffset.top;
    const elemBottom = elemTop + elemOffset.height;

    return elemBottom <= docViewBottom || elemTop >= docViewTop;
  }

  private getOffset(): number {
    const containerSize = this.isVerticalDirection(this.orientation)
      ? this.getHeight()
      : this.getWidth();

    const offset = (this.currentIndex * containerSize) / this.slidesPerView;
    const sign = this.isReversedDirection(this.orientation) ? 1 : -1;
    return sign * offset;
  }

  private getTranslation(offset: number): string {
    if (this.isVerticalDirection(this.orientation)) {
      return `translateY(${offset}px)`;
    } else {
      return `translateX(${offset}px)`;
    }
  }

  private getWidth(): number {
    return this.carouselContainer.nativeElement.clientWidth;
  }

  private getHeight(): number {
    return this.carouselContainer.nativeElement.clientHeight;
  }

  private goto(direction: Direction, index?: number): void {
    if (!this.playing) {
      const isReversedDirection = this.isReversedDirection(this.orientation);

      switch (direction) {
        case Direction.Previous:
          return isReversedDirection
            ? this.listKeyManager.setNextItemActive()
            : this.listKeyManager.setPreviousItemActive();
        case Direction.Next:
          return isReversedDirection
            ? this.listKeyManager.setPreviousItemActive()
            : this.listKeyManager.setNextItemActive();
        case Direction.Index:
          return this.listKeyManager.setActiveItem(index);
      }
    }
  }

  private playAnimation(): void {
    const translation = this.getTranslation(this.getOffset());
    const factory = this.animationBuilder.build(
      animate(this.timings, style({ transform: translation }))
    );
    const animation = factory.create(this.carouselList.nativeElement);

    animation.onStart(() => {
      this.playing = true;
      this.animationStart.emit(this.currentIndex);
    });
    animation.onDone(() => {
      this.playing = false;
      this.renderer.setStyle(
        this.carouselList.nativeElement,
        'transform',
        translation
      );
      animation.destroy();
    });
    animation.play();
  }

  private resetTimer(value: number): void {
    this.timer$ = interval(value);
  }

  private startTimer(autoplay: boolean): void {
    if (!autoplay) {
      return;
    }

    this.timer$
      .pipe(
        takeUntil(this.timerStop$),
        takeUntil(this.destroy$),
        filter(() => this.isVisible())
      )
      .subscribe(() => {
        this.listKeyManager.withWrap(true).setNextItemActive();
        this.listKeyManager.withWrap(this.loop);
      });
  }

  private stopTimer(): void {
    this.timerStop$.next();
  }

  private getKeyManagerHorizontalDirection(
    orientation: CAROUSEL_ORIENTATION
  ): 'ltr' | 'rtl' | null {
    if (this.isVerticalDirection(orientation)) {
      return null;
    }

    return orientation === 'LEFT_TO_RIGHT' ? 'ltr' : 'rtl';
  }

  private isReversedDirection(orientation: CAROUSEL_ORIENTATION): boolean {
    return orientation === 'RIGHT_TO_LEFT' || orientation === 'TOP_TO_BOTTOM';
  }
}
